import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { offerBreakResume } from '../../components/BreakResumeOffer';
import { requestBreak, requestClockOut } from '../../components/attendance/attendanceFlow';
import { apiErrorMessage } from '../../components/sales/shared';
import { usePermissions } from '../usePermissions';
import { invalidateAttendanceViews } from '../../components/attendance/attendanceInvalidate';

type ClockLocation = 'office' | 'home';

function formatElapsed(since: string): string {
  const diff = Math.max(0, Date.now() - new Date(since).getTime());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function useAttendancePanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // FU-32 — an OTE employee whose claim is `customer_manager` clocks in too
  // (the server checks the employee identity, requireOteStaff); a customer
  // login (portalOnly per /permissions/me) does not.
  const perms = usePermissions();
  const isAgentOrAdmin = user?.role === 'admin' || user?.role === 'agent'
    || (user?.role === 'customer_manager' && perms.source === 'server' && !perms.portalOnly);

  const [selectedLocation, setSelectedLocation] = useState<ClockLocation | null>(null);
  const [elapsed, setElapsed] = useState('00:00');
  const [error, setError] = useState('');

  const statusQuery = useQuery({
    queryKey: ['attendance-status'],
    queryFn: () => api.getAttendanceStatus(),
    enabled: !!isAgentOrAdmin,
    refetchInterval: 60_000,
  });

  const status = statusQuery.data?.data;

  useEffect(() => {
    if (!status?.since || status.status === 'clocked_out') return;
    const tick = () => setElapsed(formatElapsed(status.since!));
    tick();
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, [status?.since, status?.status]);

  const invalidateAttendance = useCallback(() => {
    // Break start / clock-out stop the running timers (FIX-ATT: and move the day's figures).
    invalidateAttendanceViews(queryClient);
  }, [queryClient]);

  // The server's message when it sends one; otherwise a plain default, so a
  // failure is never silent (2026-10-11).
  const fail = (fallbackKey: string) => (err: unknown) => setError(apiErrorMessage(err, t(fallbackKey)));

  const clockInMutation = useMutation({
    mutationFn: (locationType: ClockLocation) => api.clockIn(undefined, locationType),
    onSuccess: () => {
      invalidateAttendance();
      setSelectedLocation(null);
      setError('');
    },
    onError: fail('attendance.clockInFailed'),
  });

  // P-10e (D-85, D-63) — "החתמת יציאה" and "הפסקה" open the ONE window of
  // the attendance flow host (the running timers, "סגירת יום"); the host
  // stops only what the employee checked, and reports the outcome itself.
  const clockOutMutation = useMutation({
    mutationFn: () => requestClockOut(),
    onSuccess: () => setError(''),
    onError: fail('attendance.clockOutFailed'),
  });

  const breakStartMutation = useMutation({
    mutationFn: () => requestBreak(),
    onSuccess: () => setError(''),
    onError: fail('attendance.breakStartFailed'),
  });

  // D-116 — "חזרה לעבודה" after a clock-out (one clock-in per day stays).
  const backToWorkMutation = useMutation({
    mutationFn: () => api.backToWork(),
    onSuccess: () => {
      invalidateAttendance();
      setError('');
    },
    onError: fail('attendance.backToWorkFailed'),
  });

  const breakEndMutation = useMutation({
    mutationFn: () => api.endBreak(),
    onSuccess: (res) => {
      invalidateAttendance();
      setError('');
      // B.21 — offer (never auto-start) the timers the break paused.
      offerBreakResume(queryClient, res?.data?.resumable_timers);
    },
    onError: fail('attendance.breakEndFailed'),
  });

  const isPending =
    clockInMutation.isPending ||
    clockOutMutation.isPending ||
    breakStartMutation.isPending ||
    breakEndMutation.isPending ||
    backToWorkMutation.isPending;

  return {
    isAgentOrAdmin: !!isAgentOrAdmin,
    status,
    isLoading: statusQuery.isLoading,
    /** When `status` was fetched (its `today_minutes` is as of then). */
    updatedAt: statusQuery.dataUpdatedAt,
    elapsed,
    error,
    setError,
    selectedLocation,
    setSelectedLocation,
    clockIn: clockInMutation.mutate,
    clockOut: clockOutMutation.mutate,
    breakStart: breakStartMutation.mutate,
    breakEnd: breakEndMutation.mutate,
    backToWork: backToWorkMutation.mutate,
    isPending,
  };
}
